import React, { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { Box, Hidden, HStack, Skeleton, Stack, Text, VStack } from 'native-base';
import AvatarIcon from '../../AvatarIcon';
import MessageRow from '../../MessageRow';
import ESignButton from '../ESignButton/ESignButton';
import { ESignStatus, selectDisclosures, useESignDetailsQuery } from '../../../hooks';
import uuid from 'react-native-uuid';
import { ApiLogger } from '../../../utils';
import { UserDetailsContext } from '../../../containers/NewLoanInProgress';
const ESignWrapper = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    loanNumber,
    authToken,
    url,
    selected,
    logApiEvent,
    logUserEvent,
    handleOnTokenResponse,
    isEnabled,
    xApiKey
  } = _ref;
  const {
    setBorderColor,
    status: loanStatus
  } = useContext(UserDetailsContext);
  const [selectedDisclosureUserName, setSelectedDisclosureUserName] = useState('');
  const [isTokenWaiting, setTokenWaiting] = useState(false);
  const [refetchInterval, setRefetchInterval] = useState(false);
  const {
    isLoading,
    data: disclosures
  } = useESignDetailsQuery({
    url,
    logApiEvent,
    loanNumber,
    select: response => selectDisclosures(response, loanStatus),
    authToken,
    refetchInterval,
    setRefetchInterval,
    enabled: isEnabled,
    loanStatus,
    xApiKey: xApiKey
  });
  const onHide = useCallback(state => {
    if (state === 'closed') setRefetchInterval(5000);
  }, []);
  useImperativeHandle(ref, () => ({
    onHide
  }), [onHide]);
  useEffect(() => {
    logUserEvent({
      action: 'DisplayedESignDisclosures',
      type: 'ESign'
    });
  }, [logUserEvent]);
  useEffect(() => {
    if (refetchInterval) {
      setTimeout(() => {
        setRefetchInterval(false);
      }, 30 * 1000);
    }
  }, [refetchInterval]);
  useEffect(() => {
    if (loanStatus >= 13 || !disclosures) {
      setBorderColor('primary.400');
    }
    if (disclosures) {
      disclosures === null || disclosures === void 0 || disclosures.map(disclosure => {
        var _disclosure$status;
        disclosure.ribbonColor = disclosure.status && (disclosure.status === ESignStatus.COMPLETED || (_disclosure$status = disclosure.status) !== null && _disclosure$status !== void 0 && _disclosure$status.includes('Waiting for ')) ? 'primary.400' : 'border.light';
      });
      const isCompleted = disclosures.every(disclosure => {
        var _disclosure$status2;
        return disclosure.status && (disclosure.status === ESignStatus.COMPLETED || ((_disclosure$status2 = disclosure.status) === null || _disclosure$status2 === void 0 ? void 0 : _disclosure$status2.includes('Waiting for ')));
      });
      isCompleted ? setBorderColor('primary.400') : setBorderColor('border.light');
    }
  }, [disclosures, setBorderColor, loanStatus]);
  const getToken = async item => {
    setTokenWaiting(true);
    logUserEvent({
      action: 'FetchingESignTokenStarted',
      type: 'ESign',
      description: `fetching token started for url ${item.docUrl}`
    });
    try {
      if (loanNumber) {
        const correlationId = uuid.v4().toString();
        const response = await ApiLogger(logApiEvent, {
          url: `${url}/${loanNumber}/esign/token`,
          method: 'post',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'x-correlation-id': correlationId,
            'x-api-key': xApiKey
          },
          data: {
            encodedUrl: item.docUrl,
            loanNumber
          }
        });
        if (response) {
          logUserEvent({
            action: 'FetchingESignTokenSuccess',
            type: 'ESign',
            description: `fetching token started for url ${item.docUrl}`
          });
          handleTokenResponse({
            token: response.accessToken,
            url: response.eSignUrl
          });
        }
      } else {
        logUserEvent({
          action: 'FetchingESignTokenFailed',
          type: 'ESign',
          description: `using the fallback document url ${item.docUrl}`
        });
        if (item.docUrl) handleTokenResponse({
          token: '',
          url: item.docUrl
        });
      }
    } catch (e) {
      logUserEvent({
        action: 'FetchingESignTokenFailed',
        type: 'ESign',
        description: `using the fallback document url ${item.docUrl}`
      });
      if (item.docUrl) handleTokenResponse({
        token: '',
        url: item.docUrl
      });
    } finally {
      logUserEvent({
        action: 'FetchingESignTokenCompleted',
        type: 'ESign'
      });
      setTokenWaiting(false);
    }
  };
  const handleTokenResponse = useCallback(_ref2 => {
    let {
      token,
      url
    } = _ref2;
    logUserEvent({
      action: 'OpeningDocuTech',
      type: 'ESign'
    });
    handleOnTokenResponse(token, url);
  }, [handleOnTokenResponse, logUserEvent]);
  return isLoading && isEnabled ? /*#__PURE__*/React.createElement(Box, {
    borderColor: "gray.200",
    borderLeftWidth: 8,
    borderWidth: 1,
    borderRadius: "md",
    h: "24",
    p: "4",
    key: 'disclosures',
    mb: 3
  }, /*#__PURE__*/React.createElement(HStack, {
    alignItems: "center",
    justifyContent: "space-between",
    flex: 1
  }, /*#__PURE__*/React.createElement(HStack, {
    space: "4",
    alignItems: "center",
    flex: 1
  }, /*#__PURE__*/React.createElement(Skeleton, {
    w: "16",
    h: "16",
    rounded: "full"
  }), /*#__PURE__*/React.createElement(Skeleton.Text, {
    lines: 2,
    maxW: "65%"
  })), /*#__PURE__*/React.createElement(Hidden, {
    till: "md"
  }, /*#__PURE__*/React.createElement(Skeleton, {
    h: "12",
    w: "40",
    justifyContent: "flex-end",
    borderRadius: "lg"
  })), /*#__PURE__*/React.createElement(Hidden, {
    from: "md"
  }, /*#__PURE__*/React.createElement(Skeleton, {
    h: "4",
    w: "4",
    rounded: "full"
  })))) : !disclosures ? null : /*#__PURE__*/React.createElement(React.Fragment, null, disclosures.map(disclosure => {
    return /*#__PURE__*/React.createElement(Box, {
      opacity: selected ? 50 : 100,
      key: disclosure.title,
      borderRadius: "sm",
      borderTopWidth: "1",
      borderBottomWidth: "1",
      borderLeftWidth: "8",
      borderRightWidth: "1",
      borderTopColor: "border.light",
      borderBottomColor: "border.light",
      borderRightColor: "border.light",
      borderLeftColor: disclosure.ribbonColor,
      mb: "3",
      pl: "3",
      py: "3",
      pr: "8",
      w: "100%",
      bg: "white.700"
    }, /*#__PURE__*/React.createElement(Hidden, {
      from: "sm"
    }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HStack, {
      space: "md",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(AvatarIcon, {
      name: disclosure.name
    })), /*#__PURE__*/React.createElement(Text, {
      fontFamily: "body",
      fontWeight: "medium",
      fontSize: "sm",
      lineHeight: "2xs",
      color: "text.running",
      opacity: 100,
      accessibilityLabel: disclosure.name,
      testID: disclosure.name
    }, disclosure.name)), disclosure.userName ? /*#__PURE__*/React.createElement(HStack, {
      ml: "3",
      mt: "2"
    }, /*#__PURE__*/React.createElement(Text, {
      fontFamily: "body",
      fontWeight: "normal",
      lineHeight: "lg",
      opacity: 100,
      paddingY: '0.5',
      fontSize: "sm",
      marginRight: '1'
    }, "Borrower:"), /*#__PURE__*/React.createElement(Text, {
      fontFamily: "body",
      fontWeight: "normal",
      fontSize: "13",
      lineHeight: "lg",
      color: "primary.400",
      alignSelf: 'flex-start',
      borderWidth: "1",
      borderRadius: '12',
      borderColor: "primary.400",
      opacity: 100,
      paddingX: '1',
      paddingY: '0.5',
      accessibilityLabel: disclosure.userName,
      testID: disclosure.userName
    }, disclosure.userName)) : null, /*#__PURE__*/React.createElement(VStack, {
      flex: "1",
      space: "3",
      mt: "2",
      ml: "3"
    }, !disclosure.details ? null : /*#__PURE__*/React.createElement(Text, {
      fontSize: "sm",
      fontWeight: "normal",
      fontFamily: "body",
      color: "black.700",
      opacity: 100,
      accessibilityLabel: disclosure.details,
      testID: disclosure.details
    }, disclosure.details), disclosure.infoText ? /*#__PURE__*/React.createElement(MessageRow, {
      text: disclosure.infoText,
      color: disclosure.color,
      opacity: 100,
      maxW: "85%"
    }) : null, /*#__PURE__*/React.createElement(ESignButton, {
      status: disclosure.status,
      isTokenWaiting: selectedDisclosureUserName === disclosure.userName ? isTokenWaiting : false,
      isRefetching: selectedDisclosureUserName === disclosure.userName ? refetchInterval : false,
      disclosures: disclosure,
      getToken: getToken,
      selected: selected,
      setSelectedDisclosureUserName: setSelectedDisclosureUserName
    })))), /*#__PURE__*/React.createElement(Hidden, {
      only: ['base', 'sm']
    }, /*#__PURE__*/React.createElement(Stack, {
      space: "4",
      direction: {
        base: 'column',
        md: 'row',
        lg: 'row'
      },
      alignItems: {
        base: 'flex-start',
        md: 'center',
        lg: 'center'
      },
      paddingY: '3'
    }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(AvatarIcon, {
      name: disclosure.name
    })), /*#__PURE__*/React.createElement(VStack, {
      flex: "1",
      space: "2.5"
    }, /*#__PURE__*/React.createElement(VStack, {
      flex: "1",
      space: "1"
    }, /*#__PURE__*/React.createElement(Text, {
      fontFamily: "body",
      fontWeight: "medium",
      fontSize: "md",
      lineHeight: "xs",
      color: "text.running",
      opacity: 100,
      accessibilityLabel: disclosure.name,
      testID: disclosure.name
    }, disclosure.name), disclosure.userName ? /*#__PURE__*/React.createElement(HStack, null, /*#__PURE__*/React.createElement(Text, {
      fontFamily: "body",
      fontWeight: "normal",
      lineHeight: "lg",
      opacity: 100,
      paddingY: '0.5',
      fontSize: "sm",
      marginRight: '1'
    }, "Borrower:"), /*#__PURE__*/React.createElement(Text, {
      fontFamily: "body",
      fontWeight: "normal",
      fontSize: "13",
      lineHeight: "lg",
      color: "primary.400",
      alignSelf: 'flex-start',
      borderWidth: "1",
      borderRadius: '16',
      borderColor: "primary.400",
      opacity: 100,
      paddingX: '1',
      paddingY: '0.5',
      accessibilityLabel: disclosure.userName,
      testID: disclosure.userName
    }, disclosure.userName)) : null), !disclosure.details ? null : /*#__PURE__*/React.createElement(Text, {
      fontSize: "sm",
      fontWeight: "normal",
      fontFamily: "body",
      color: "black.700",
      opacity: 100,
      accessibilityLabel: disclosure.details,
      testID: disclosure.details,
      maxW: "85%"
    }, disclosure.details), disclosure.infoText && !refetchInterval ? /*#__PURE__*/React.createElement(MessageRow, {
      text: disclosure.infoText,
      color: disclosure.color,
      opacity: 100,
      maxW: "85%"
    }) : null), /*#__PURE__*/React.createElement(ESignButton, {
      status: disclosure.status,
      isTokenWaiting: selectedDisclosureUserName === disclosure.userName ? isTokenWaiting : false,
      isRefetching: selectedDisclosureUserName === disclosure.userName ? refetchInterval : false,
      disclosures: disclosure,
      getToken: getToken,
      selected: selected,
      setSelectedDisclosureUserName: setSelectedDisclosureUserName
    }))));
  }));
});
export default /*#__PURE__*/memo(ESignWrapper);
//# sourceMappingURL=index.js.map